import { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import {
  Shield, LayoutDashboard, Database, Users, Layers, FileText,
  Link2, Wallet, BarChart3, Cpu, Gift, Clock, Webhook, Fuel,
  ChevronLeft, ChevronRight, Radio, Activity
} from 'lucide-react';
import LoginPanel from '../components/LoginPanel';

const NAV_CONFIG = {
  admin: {
    label: 'ADMIN',
    color: '#f97316',
    links: [
      { path: '/commander/admin',           icon: LayoutDashboard, label: 'Dashboard',        end: true },
      { path: '/commander/admin/database',  icon: Database,        label: 'Database Control'  },
      { path: '/commander/admin/agencies',  icon: Users,           label: 'Agency Onboarding' },
      { path: '/commander/admin/tiers',     icon: Layers,          label: 'Tier Management'   },
      { path: '/commander/admin/wallets',   icon: Wallet,          label: 'Wallets'           }, // ✅ FIX: was 404
      { path: '/commander/admin/sla-logs',  icon: FileText,        label: 'SLA Logs'          },
    ],
  },
  affiliate: {
    label: 'AFFILIATE',
    color: '#06b6d4',
    links: [
      { path: '/commander/affiliate',        icon: BarChart3, label: 'SLA Dashboard',  end: true },
      { path: '/commander/affiliate/links',  icon: Link2,     label: 'Link Analytics'  },
      { path: '/commander/affiliate/escrow', icon: Wallet,    label: 'Escrow Ledger'   },
    ],
  },
  client: {
    label: 'CLIENT',
    color: '#eab308',
    links: [
      { path: '/commander/client',           icon: Cpu,     label: 'API Routing',     end: true },
      { path: '/commander/client/fuel',      icon: Fuel,    label: 'Node Fuel Status' },
      { path: '/commander/client/webhooks',  icon: Webhook, label: 'Webhooks'         },
    ],
  },
  investor: {
    label: 'INVESTOR',
    color: '#f97316',
    links: [
      { path: '/commander/investor',         icon: Activity, label: 'Telemetry Matrix', end: true },
      { path: '/commander/investor/genesis', icon: Gift,     label: 'Genesis Invites'  },
      { path: '/commander/investor/yield',   icon: Clock,    label: 'Yield History'    },
    ],
  },
};

export default function CommanderLayout() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [systemStatus] = useState('OPERATIONAL');

  // Determine active role from URL
  const pathParts = location.pathname.split('/');
  const activeRole = pathParts[2] || 'admin';
  const config = NAV_CONFIG[activeRole] || NAV_CONFIG.admin;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#0f0f0f', color: '#e5e5e5', fontFamily: 'monospace' }}>
      {/* Sidebar */}
      <aside style={{
        width: collapsed ? 64 : 220,
        transition: 'width 0.2s',
        background: '#141414',
        borderRight: '1px solid #222',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 0',
        flexShrink: 0,
      }}>
        {/* Logo / Brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px 16px', borderBottom: '1px solid #222' }}>
          <Shield size={20} color={config.color} />
          {!collapsed && (
            <span style={{ fontSize: 13, fontWeight: 700, color: config.color, letterSpacing: 2 }}>
              {config.label}
            </span>
          )}
        </div>

        {/* Nav Links */}
        <nav style={{ flex: 1, padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {config.links.map(({ path, icon: Icon, label, end }) => (
            <NavLink
              key={path}
              to={path}
              end={end}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                borderRadius: 6,
                textDecoration: 'none',
                fontSize: 13,
                color: isActive ? config.color : '#888',
                background: isActive ? `${config.color}18` : 'transparent',
                fontWeight: isActive ? 600 : 400,
                transition: 'all 0.15s',
              })}
            >
              <Icon size={16} />
              {!collapsed && <span>{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Status + Collapse Toggle */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #222', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {!collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#555' }}>
              <Radio size={11} color="#22c55e" />
              <span style={{ color: '#22c55e' }}>{systemStatus}</span>
            </div>
          )}
          <button
            onClick={() => setCollapsed(v => !v)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#555', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', padding: 0 }}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            {!collapsed && <span style={{ fontSize: 12, marginLeft: 6 }}>Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main style={{ flex: 1, overflow: 'auto' }}>
        <Outlet />
      </main>
    </div>
  );
}
